// useSnapScroll.ts
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export interface ScrollItem {
  id: string | number
  [key: string]: any
}

export interface VirtualItem {
  index: number
  start: number
  key: string | number
  item: ScrollItem
}

export interface GroupBoundary {
  startIndex: number
  endIndex: number
  groupIndex: number
}

export interface ScrollOptions {
  align?: 'start' | 'center' | 'end' | 'auto'
  behavior?: 'auto' | 'smooth'
}

export type ScrollToIndexFunction = (
  index: number,
  options?: ScrollOptions
) => void

export interface SnapScrollState {
  itemsPerGroup: number
  currentGroup: number
  totalGroups: number
  isNavigating: boolean
  canScrollNext: boolean
  canScrollPrevious: boolean
}

export interface SnapScrollActions {
  scrollNext: () => void
  scrollPrevious: () => void
  scrollToGroup: (groupIndex: number) => void
}

export interface ItemRenderProps {
  item: ScrollItem
  index: number
  isSnapPoint: boolean
  style: React.CSSProperties
}

export interface UseSnapScrollOptions {
  totalSize: number
  totalItems: number
  itemWidth: number
  gap: number
  scrollToIndex?: ScrollToIndexFunction | null
  onGroupChange?: (groupIndex: number) => void
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void
  containerRef: React.RefObject<HTMLDivElement>
}

export interface UseSnapScrollReturn {
  state: SnapScrollState
  actions: SnapScrollActions
  handleScroll: (event: React.UIEvent<HTMLDivElement>) => void
  handleKeyDown: (event: React.KeyboardEvent) => void
  handleTouchStart: (event: React.TouchEvent) => void
  handleTouchEnd: (event: React.TouchEvent) => void
  getItemProps: (virtualItem: VirtualItem) => ItemRenderProps
}

export const useSnapScroll = ({
  totalSize,
  totalItems,
  itemWidth,
  gap,
  scrollToIndex = null,
  onGroupChange,
  onScroll,
  containerRef
}: UseSnapScrollOptions): UseSnapScrollReturn => {
  const [itemsPerGroup, setItemsPerGroup] = useState<number>(1)
  const [currentGroup, setCurrentGroup] = useState<number>(0)
  const [totalGroups, setTotalGroups] = useState<number>(0)
  const [isNavigating, setIsNavigating] = useState<boolean>(false)
  const touchStart = useRef<{ x: number; y: number }>({ x: 0, y: 0 })

  // Auto-detect how many items are fully visible
  const detectVisibleItems = useCallback((): number => {
    if (!containerRef.current) return 1

    const containerWidth = containerRef.current.getBoundingClientRect().width
    let fullyVisibleCount = 0
    let totalWidthUsed = 0

    for (let i = 0; i < totalItems; i++) {
      if (totalWidthUsed + itemWidth <= containerWidth) {
        fullyVisibleCount++
        totalWidthUsed += itemWidth + (i < totalItems - 1 ? gap : 0)
      } else {
        break
      }
    }

    return Math.max(1, fullyVisibleCount)
  }, [totalItems, itemWidth, gap, containerRef])

  // Calculate group boundaries
  const groupBoundaries = useMemo((): GroupBoundary[] => {
    const boundaries: GroupBoundary[] = []
    for (let i = 0; i < Math.ceil(totalItems / itemsPerGroup); i++) {
      const startIndex = i * itemsPerGroup
      const endIndex = Math.min(startIndex + itemsPerGroup - 1, totalItems - 1)
      boundaries.push({ startIndex, endIndex, groupIndex: i })
    }
    return boundaries
  }, [totalItems, itemsPerGroup])

  // Get current group based on scroll position
  const getCurrentGroup = useCallback((): number => {
    if (!containerRef.current || groupBoundaries.length === 0) return 0

    const scrollLeft = containerRef.current.scrollLeft
    const containerWidth = containerRef.current.getBoundingClientRect().width
    const tolerance = 10

    // Check if we're at the end
    const maxScroll = totalSize - containerWidth
    if (scrollLeft >= maxScroll - tolerance) {
      return totalGroups - 1
    }

    // Find the closest group
    let bestGroup = 0
    let minDistance = Infinity

    groupBoundaries.forEach(boundary => {
      const groupStartPosition = boundary.startIndex * (itemWidth + gap)
      const distance = Math.abs(scrollLeft - groupStartPosition)

      if (distance < minDistance) {
        minDistance = distance
        bestGroup = boundary.groupIndex
      }
    })

    return bestGroup
  }, [groupBoundaries, totalGroups, itemWidth, gap, totalSize, containerRef])

  // Scroll to specific group
  const scrollToGroup = useCallback(
    (groupIndex: number): void => {
      if (!groupBoundaries.length) return

      groupIndex = Math.min(groupIndex, totalGroups - 1)
      groupIndex = Math.max(groupIndex, 0)

      const boundary = groupBoundaries[groupIndex]
      if (!boundary) return

      setIsNavigating(true)

      let targetIndex = boundary.startIndex

      // Special handling for the last group
      if (groupIndex === totalGroups - 1) {
        const itemsInLastGroup = totalItems - groupIndex * itemsPerGroup
        const containerWidth =
          containerRef.current?.getBoundingClientRect().width || 0

        if (itemsInLastGroup < itemsPerGroup && currentGroup !== groupIndex) {
          const totalWidthNeeded =
            itemsInLastGroup * itemWidth + (itemsInLastGroup - 1) * gap

          if (totalWidthNeeded > containerWidth) {
            targetIndex = Math.max(0, totalItems - itemsPerGroup)
          }
        }
      }

      // Use external scroll function or native scrollTo
      if (scrollToIndex) {
        scrollToIndex(targetIndex, { align: 'start', behavior: 'smooth' })
      } else if (containerRef.current) {
        const targetPosition = targetIndex * (itemWidth + gap)
        containerRef.current.scrollTo({
          left: targetPosition,
          behavior: 'smooth'
        })
      }

      setCurrentGroup(groupIndex)
      onGroupChange?.(groupIndex)

      setTimeout(() => setIsNavigating(false), 600)
    },
    [
      groupBoundaries,
      totalGroups,
      totalItems,
      itemsPerGroup,
      currentGroup,
      itemWidth,
      gap,
      scrollToIndex,
      onGroupChange,
      containerRef
    ]
  )

  // Detection and update
  const detectAndUpdate = useCallback((): void => {
    const newItemsPerGroup = detectVisibleItems()
    if (newItemsPerGroup !== itemsPerGroup || totalGroups === 0) {
      const oldItemsPerGroup = itemsPerGroup
      setItemsPerGroup(newItemsPerGroup)

      const newTotalGroups = Math.ceil(totalItems / newItemsPerGroup)
      setTotalGroups(newTotalGroups)

      if (oldItemsPerGroup > 0 && newTotalGroups > 0) {
        const currentItemIndex = currentGroup * oldItemsPerGroup
        const newGroup = Math.min(
          Math.floor(currentItemIndex / newItemsPerGroup),
          newTotalGroups - 1
        )
        setCurrentGroup(newGroup)
        onGroupChange?.(newGroup)
      }
    }
  }, [
    detectVisibleItems,
    itemsPerGroup,
    totalGroups,
    totalItems,
    currentGroup,
    onGroupChange
  ])

  // Navigation functions
  const scrollNext = useCallback((): void => {
    if (currentGroup < totalGroups - 1) {
      scrollToGroup(currentGroup + 1)
    }
  }, [currentGroup, totalGroups, scrollToGroup])

  const scrollPrevious = useCallback((): void => {
    if (currentGroup > 0) {
      scrollToGroup(currentGroup - 1)
    }
  }, [currentGroup, scrollToGroup])

  // Event handlers
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>): void => {
      onScroll?.(e)

      if (isNavigating) return

      const newGroup = getCurrentGroup()
      if (newGroup !== currentGroup) {
        setCurrentGroup(newGroup)
        onGroupChange?.(newGroup)
      }
    },
    [getCurrentGroup, currentGroup, isNavigating, onScroll, onGroupChange]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent): void => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        scrollPrevious()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        scrollNext()
      }
    },
    [scrollPrevious, scrollNext]
  )

  const handleTouchStart = useCallback((e: React.TouchEvent): void => {
    touchStart.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    }
  }, [])

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent): void => {
      const endX = e.changedTouches[0].clientX
      const endY = e.changedTouches[0].clientY
      const diffX = touchStart.current.x - endX
      const diffY = touchStart.current.y - endY

      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
        if (diffX > 0) {
          scrollNext()
        } else {
          scrollPrevious()
        }
      }
    },
    [scrollNext, scrollPrevious]
  )

  // Get item props function
  const getItemProps = useCallback(
    (virtualItem: VirtualItem): ItemRenderProps => {
      const isSnapPoint = virtualItem.index % itemsPerGroup === 0

      return {
        item: virtualItem.item,
        index: virtualItem.index,
        isSnapPoint,
        style: {
          position: 'absolute',
          left: `${virtualItem.start}px`,
          width: `${itemWidth}px`,
          top: 0,
          scrollSnapAlign: isSnapPoint ? 'start' : 'none'
        }
      }
    },
    [itemWidth, itemsPerGroup]
  )

  // State object
  const state: SnapScrollState = {
    itemsPerGroup,
    currentGroup,
    totalGroups,
    isNavigating,
    canScrollNext: currentGroup < totalGroups - 1,
    canScrollPrevious: currentGroup > 0
  }

  // Actions object
  const actions: SnapScrollActions = {
    scrollNext,
    scrollPrevious,
    scrollToGroup
  }

  // Effects
  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout
    const debouncedHandleResize = (): void => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(detectAndUpdate, 250)
    }

    window.addEventListener('resize', debouncedHandleResize)
    return () => {
      window.removeEventListener('resize', debouncedHandleResize)
      clearTimeout(resizeTimeout)
    }
  }, [detectAndUpdate])

  useEffect(() => {
    const timer = setTimeout(detectAndUpdate, 100)
    return () => clearTimeout(timer)
  }, [detectAndUpdate])

  return {
    state,
    actions,
    handleScroll,
    handleKeyDown,
    handleTouchStart,
    handleTouchEnd,
    getItemProps
  }
}
